import React, { useMemo } from 'react';
import cx from 'classnames';
import _sumBy from 'lodash/sumBy';

import { getStatusRanges, getStatusMeterChunks } from './helpers';

/**
 * @typedef {DetectionStatusMeterProps}
 * @property {number} height optional height of meter bar
 * @property {boolean} grayscale whether to display the component in shades of gray
 * @property {DetectionStatusChange[]} detectionHistory array of DetectionStatusChange elements,
 * typically from `trqkSong.detection_history`
 * @property {number} width optional width of meter bar
 */
interface DetectionStatusMeterProps {
    height?: number;
    grayscale?: boolean;
    detectionHistory: DetectionStatusChange[];
    width?: number;
}

/**
 * Renders a horizontal meter showing details of the DetectionStatusDateRange elements that fall
 * within the current quarter. The meter is separated into three kinds of chunks:
 * | Type               | BG (Color) | BG (Grayscale) |
 * |--------------------|------------|----------------|
 * | detection active   | teal       | dark gray      |
 * | detection inactive | gold       | mid gray       |
 * | future period      | light blue | light gray     |
 *
 * A sum of all active detection percentages is also shown next to the meter.
 * @param {DetectionStatusMeterProps} props
 */
export const DetectionStatusMeter = ({
    height = 6,
    detectionHistory,
    grayscale = false,
    width = 100,
}: DetectionStatusMeterProps): JSX.Element => {
    const chunks = useMemo((): DetectionStatusMeterChunk[] => {
        const statusRanges = getStatusRanges(detectionHistory);

        return getStatusMeterChunks(statusRanges);
    }, [detectionHistory]);

    const activePercent = _sumBy(
        chunks,
        ({ active, percent }: DetectionStatusMeterChunk): number => {
            if (active) {
                return percent;
            }

            return 0;
        },
    );

    return (
        <div className="flex items-center">
            <div className="flex" style={{ width, height }}>
                {chunks.map(
                    (
                        {
                            id,
                            active,
                            percent,
                            future,
                        }: DetectionStatusMeterChunk,
                        index: number,
                    ): JSX.Element => (
                        <div
                            key={id}
                            className={cx({
                                'bg-secondary-500': !grayscale && active,
                                'bg-highlight-gold':
                                    !grayscale && !active && !future,
                                'bg-quarternary-100': !grayscale && future,
                                'bg-tertiary-400': grayscale && active,
                                'bg-tertiary-300':
                                    grayscale && !active && !future,
                                'bg-tertiary-100': grayscale && future,
                                'rounded-l-full': index === 0,
                                'rounded-r-full': index === chunks.length - 1,
                            })}
                            style={{
                                width: (percent / 100) * width,
                                height,
                            }}
                        />
                    ),
                )}
            </div>
            <div
                className={cx('ml-2 font-semibold text-xs', {
                    'text-secondary-500': !grayscale,
                    'text-tertiary-400': grayscale,
                })}
            >
                {`${activePercent}%`}
            </div>
        </div>
    );
};
